import { getElement, getElements, globalEvent } from '@shared/easyfy.core';
import { invoke } from '@shared/easyfy.decorators';
import { debounce, hide, show } from '@shared/easyfy.utils';
import * as ListUtils from '@webModules/HTMLListElementExtension';
import filterService from '@webServices/filter.service';
import noUiSlider, { API } from 'nouislider';

namespace Easyfy.Modules {

  export enum StateType {
    Scroll,
    Open
  }

  export enum SortDirection {
    "Ascending" = 0,
    "Descending" = 1
  }

  export enum FilterFor {
    Category,
    Brand,
    Search,
    Articles
  }

  export interface IElementState {
    elementId: string;
    stateType: StateType;
    value: any;
  }

  export interface Sorter {
    key: string;
    sortDirection: string;
  }

  @invoke

  export class Filter {
    static Instance: Filter;

    private static FilterQuerySelector: string;

    private static PagingQuerySelector: string;

    private static ItemsQuerySelector: string;

    private static SortersQuerySelector: string;

    private static readonly FilterCheckboxQuerySelector: string = 'input[type="checkbox"][data-action="change-filter"]';

    private static FilterFor: FilterFor;

    private static Page: number;

    private static searchCharsThreshold: number;

    private static Sorter: Sorter;

    private static SearchString: string;

    private static elementsStates: IElementState[];

    private static readonly defaultBrandItemsShown: number = 6;

    private static brandItemsShown: number | null = Filter.defaultBrandItemsShown;

    private static filterState: Record<string, string> = {};

    private static sliderInstance: API;

    constructor(filterFor: FilterFor, filterQuerySelector: string, pagingQuerySelector: string, itemsQuerySelector: string, sortersQuerySelector: string, searchCharsThreshold: number = 3) {
      Filter.FilterQuerySelector = filterQuerySelector;
      Filter.PagingQuerySelector = pagingQuerySelector;
      Filter.ItemsQuerySelector = itemsQuerySelector;
      Filter.SortersQuerySelector = sortersQuerySelector;
      Filter.Page = 1;
      Filter.FilterFor = filterFor;
      Filter.searchCharsThreshold = searchCharsThreshold;

      // Initialize hierarchical status.
      Filter.updateHierarchicalStatus();

      // Reduce the initially visible number of brands.
      Filter.brandFilterTakeTop('details[data-key="Brand"]', Filter.brandItemsShown);
    }

    onInit() {

      globalEvent({
        type: 'click',
        event: {
          action: 'load-more-items',
          callback: Filter.loadMoreItems
        }
      });

      globalEvent({
        type: 'change',
        event: {
          action: 'change-filter',
          callback: Filter.changeFilter
        }
      });

      globalEvent({
        type: 'click',
        event: {
          action: 'clear-filters',
          callback: Filter.clearFilters
        }
      });

      globalEvent({
        type: 'click',
        event: {
          action: 'clear-single-filter',
          callback: Filter.clearSingleFilter
        }
      });

      globalEvent({
        type: 'change',
        event: {
          action: 'change-sort',
          callback: Filter.changeSort
        }
      });

      globalEvent({
        type: 'keyup',
        event: {
          action: 'filter-search-input',
          callback: debounce(Filter.onSearch, 500)
        }
      });

      globalEvent({
        type: 'keyup',
        event: {
          action: 'search-product-subset',
          callback: debounce(Filter.onSearch, 500)
        }
      });

      globalEvent({
        type: 'keyup',
        event: {
          action: 'category-filter-search-input',
          callback: debounce(Filter.onSearchV2, 500)
        }
      });

      globalEvent({
        type: 'click',
        event: {
          action: 'toggle-child-filters',
          callback: Filter.toggleFilterChildren
        }
      });

      globalEvent({
        type: 'click',
        event: {
          action: 'brand-filter-show-all',
          callback: Filter.onBrandFilterShowAll
        }
      });

      globalEvent({
        type: 'click',
        event: {
          action: 'brand-filter-show-less',
          callback: Filter.onBrandFilterShowLess
        }
      });

      globalEvent({
        type: 'change',
        event: {
          action: 'set-price-filter-option',
          callback: Filter.onSetPriceFilterOption
        }
      });

      document.addEventListener("change-sort-option", (e) => {
        Filter.changeSort(e);
      });

      Filter.initPriceFilter();
    };

    private static GetProductListName(): string {
      return getElement<HTMLElement>(Filter.ItemsQuerySelector)?.getAttribute("data-list-name") || '';
    };

    private static initPriceFilter() {
      const slider = document.getElementById('priceFilterSlider') as HTMLElement;
      if (!slider) {
        return;
      }
      let minValue = Number(slider.getAttribute('data-min-value') ?? undefined);
      let maxValue = Number(slider.getAttribute('data-max-value') ?? undefined);
      const currentMinValue = Number(slider.getAttribute('data-current-min-value') ?? undefined);
      const currentMaxValue = Number(slider.getAttribute('data-current-max-value') ?? undefined);

      // When the slider instance exists (when a user has changed values in it) we take the min max from its current state so the range is the same.
      if (Filter.sliderInstance?.options.range.max) {
        maxValue = Number(Filter.sliderInstance.options.range.max);
      }

      if (Filter.sliderInstance?.options.range.min) {
        minValue = Number(Filter.sliderInstance.options.range.min);
      }

      // Initialize noUiSlider
      Filter.sliderInstance = noUiSlider.create(slider, {
        start: [(currentMinValue ?? minValue), (currentMaxValue ?? maxValue)], // Initial range
        connect: true, // Connect the handles
        range: {
          min: minValue,
          max: maxValue,
        },
        step: 1, // Increment step
      });

      const minValueDisplay = document.getElementById('min-value') as HTMLInputElement;
      const maxValueDisplay = document.getElementById('max-value') as HTMLInputElement;

      // listen to all updates in nouislider
      Filter.sliderInstance.on('update', (values) => {
        minValueDisplay.value = `${Number(values[0])} kr`;
        maxValueDisplay.value = `${Number(values[1])} kr`;
        slider.setAttribute('data-current-min-value', Number(values[0]).toString());
        slider.setAttribute('data-current-max-value', Number(values[1]).toString());
      });

      // trigger search when value has been set
      Filter.sliderInstance.on('set', () => {
        Filter.Page = 1;
        const productListName: string = Filter.GetProductListName();
        const pageId: string | undefined = slider.getAttribute('data-page-id') ?? undefined;
        Filter.RefreshData(pageId, productListName, true, null).catch(console.error);
      });
    }

    private static onSetPriceFilterOption(e: Event) {
      const radioOption: HTMLInputElement = e.target as HTMLInputElement;

      if (!radioOption) {
        return;
      }

      if (radioOption.value.startsWith("-")) {
        // Value is less than the number after the dash.
        const lessThan = parseInt(radioOption.value.substring(1), 10);
        const min = Number(Filter.sliderInstance.options.range.min);
        Filter.sliderInstance.set([min, null], false);
        Filter.sliderInstance.set([null, lessThan]);
      } else if (radioOption.value.endsWith("-")) {
        // Value is greater than the number before the dash.
        const greaterThan = parseInt(radioOption.value.substring(0, radioOption.value.length - 1), 10);
        const max = Number(Filter.sliderInstance.options.range.max);
        Filter.sliderInstance.set([greaterThan, null], false);
        Filter.sliderInstance.set([null, max]);
      } else if (radioOption.value.includes("-")) {
        // Value is between two numbers.
        const [min, max] = radioOption.value.split("-").map((num) => parseInt(num, 10));
        Filter.sliderInstance.set([min, null], false);
        Filter.sliderInstance.set([null, max]);
      }
    };

    private static onSearch(e: Event) {
      e.preventDefault();
      e.stopPropagation();

      const input: HTMLInputElement = e.target as HTMLInputElement;
      const pageId: string | undefined = input.getAttribute('data-page-id') ?? undefined;
      const productListName: string = Filter.GetProductListName();

      Filter.Page = 1;
      Filter.SearchString = input.value.trim() ?? '';

      if (Filter.SearchString && Filter.SearchString?.length >= Filter.searchCharsThreshold) {

        Filter.RefreshData(pageId, productListName, true, null).catch(console.error);

      } else if (Filter.SearchString === '') {
        Filter.RefreshData(pageId, productListName, true, null).catch(console.error);
      }
    };

    private static filterListV2(ulElement: HTMLUListElement, searchText: string): boolean {

      // Overrides when searchText is empty.
      if (searchText.length === 0) {
        switch (ulElement.id) {
          case 'ul-for-Brand':
            Filter.brandFilterTakeTop('details[data-key="Brand"]', Filter.brandItemsShown);
            return false;
          case 'ul-for-Categories':
            Filter.collapseCategory(ulElement);
            return false;
          default:
            break;
        }
      }

      let hasMatch = false;

      const listItems = ulElement.querySelectorAll<HTMLLIElement>('li.filter-row');

      listItems.forEach((li: HTMLLIElement) => {
        const checkbox = li.querySelector('input[type="checkbox"]') as HTMLInputElement;
        const itemMatches = checkbox && checkbox.value.toLowerCase().includes(searchText);

        // Check if this `li` has a nested `ul` and search within it.
        const nestedUl = li.querySelector('ul');
        let childMatches = false;
        if (nestedUl) {
          // Recursively filter the nested list.
          childMatches = Filter.filterListV2(nestedUl, searchText);
        }

        // Set visibility of this `li` based on whether it matches or any child matches.
        if (itemMatches || childMatches) {
          // Show the `li` if it matches.
          li.setAttribute('data-state', 'open');
          li.classList.remove('hidden');
          hasMatch = true;
        } else {
          // Hide the `li` if it doesn't match.
          li.classList.add('hidden');
          li.setAttribute('data-state', 'closed');
        }
      });
      return hasMatch;
    }

    private static restoreSearchStateV2() {

      const searchInputs = document.querySelectorAll('input[type="text"][data-action="category-filter-search-input"]');

      searchInputs.forEach((input) => {
        const inputElement = input as HTMLInputElement;

        const savedValue = Filter.filterState[inputElement.id];
        if (savedValue) {
          inputElement.value = savedValue;

          const filterContent = inputElement.closest('.filter-content');
          const ulElement = filterContent?.querySelector('ul.filter-rows') as HTMLUListElement;
          if (ulElement) {
            Filter.filterListV2(ulElement, savedValue);
          }
        }
      });
    };

    private static onSearchV2(e: Event) {
      e.preventDefault();
      e.stopPropagation();

      const input = e.target as HTMLInputElement;
      const searchText = input.value.trim().toLowerCase() ?? '';

      Filter.filterState[input.id] = searchText;

      const filterContent = input.closest('.filter-content');
      if (!filterContent) {
        return;
      }

      const clearFilterButton = filterContent.querySelector('.clear-single-filter');

      if (!searchText) {
        hide(clearFilterButton);
      } else {
        show(clearFilterButton);
      }

      const ulElement = filterContent.querySelector('ul.filter-rows') as HTMLUListElement;
      if (!ulElement) {
        return;
      }

      Filter.filterListV2(ulElement, searchText);
    };


    /**
     * Explicitly collapses a specific filter item, while making sure its children are ready to be viewed if the item is expanded.
     * @param {HTMLUListElement} ulElement The filter ul element that should be collapsed.
     */
    private static collapseCategory = (ulElement: HTMLUListElement) => {
      const descendantLis = ulElement.querySelectorAll('li');
      descendantLis.forEach(li => {
        li.setAttribute('data-state', 'closed');
        li.classList.remove('hidden');

      });
    };

    private static loadMoreItems = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();

      let button: HTMLElement = e.target as HTMLElement;

      if (!button.hasAttribute('data-page-id')) {
        button = button.parentElement as HTMLElement;
      }

      const pageId: string | undefined = button.getAttribute('data-page-id') ?? undefined;
      const productListName: string = Filter.GetProductListName();
      Filter.Page = (button.getAttribute('data-page') ?? undefined) as unknown as number;
      Filter.RefreshData(pageId, productListName, false, null).catch(console.error);

      return false;
    };

    private static changeFilter = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();

      const input: HTMLElement = e.target as HTMLElement;

      // Check if the input is a HTMLInputElement and if it's a checkbox
      if (input instanceof HTMLInputElement && input.type === 'checkbox') {
        // Set the data-state attribute to "checked" or "unchecked" based on the checkbox value
        input.setAttribute('data-state', input.checked ? 'checked' : '');

        // Automatically update children if there are any
        const filterIsParent = input.parentElement.classList.contains('filter-item');
        if (filterIsParent) {
          const childCheckboxes = input.parentElement.parentElement.querySelectorAll<HTMLInputElement>('.filter-child-rows .filter-item [data-action="change-filter"]');
          childCheckboxes.forEach(checkbox => {
            checkbox.checked = input.checked;
            checkbox.setAttribute('data-state', input.checked ? 'checked' : '');
          });
        }
      }

      const pageId: string | undefined = input.getAttribute('data-page-id') ?? undefined;
      const productListName: string = Filter.GetProductListName();

      const filterGroup = input.closest('[data-container="filter-group"]') as HTMLElement;
      const counts = Filter.GetFacetCountsForCurrentGroup(filterGroup.id);

      Filter.Page = 1;
      Filter.RefreshData(pageId, productListName, true, counts).catch(console.error);

      return false;
    };

    private static GetFacetCountsForCurrentGroup = (facetGroupKey: string): filterService.IFacetGroupCount => {

      const filterGroup = getElement<HTMLElement>(`.filter-groups #${facetGroupKey}`);
      if (!filterGroup) return null;

      const countData: Array<filterService.IFacetValueCount> = [];

      const inputs = filterGroup.querySelectorAll<HTMLInputElement>('.filter-row [data-action="change-filter"]');

      const isAnyChecked = Array.from(inputs).some(input => input.checked);

      if (isAnyChecked === false) {
        return null;
      }

      inputs.forEach(filterInput => {
        countData.push({
          value: filterInput.getAttribute('value') ?? '',
          count: Number.parseInt(filterInput.getAttribute('data-result') ?? '', 10) ?? 0
        });
      });

      return {
        key: facetGroupKey,
        valueCountData: countData
      };
    };

    private static RefreshData = async (pageId: string | undefined, productListName: string, replaceProducts: boolean = true, facetGroupCount: filterService.IFacetGroupCount = null): Promise<void> => {
      if (!pageId?.trim()) {
        throw new Error('Filter.RefreshData: pageId cannot be empty.');
      }
      if (!productListName?.trim()) {
        throw new Error('Filter.RefreshData: productListName cannot be empty.');
      }

      Filter.saveFilterState();
      const urlParams = new URLSearchParams(window.location.search);
      const searchQuery = urlParams.get('q');
      const discountId = urlParams.get('discountId');
      const QueryParams: filterService.IQuery = {
        checkedFilters: Filter.getFilterData(),
        page: Filter.Page,
        sorter: {
          key: Filter.Sorter?.key ?? null,
          sortdirection: SortDirection[Filter.Sorter?.sortDirection as keyof typeof SortDirection]
        },
        searchString: Filter.SearchString ?? searchQuery,
        pageSize: 40
      };

      if (facetGroupCount) {
        QueryParams.applyFilterGroupCount = facetGroupCount;
      }

      if (QueryParams.sorter.key == null || QueryParams.sorter.sortdirection == null) {
        QueryParams.sorter = null;
      }

      const response = await Filter.getItems(pageId, productListName, QueryParams, discountId);
      if (response.status === 200 && response.data != null && response.data.success) {
        const filterContainer = getElement<HTMLElement>(Filter.FilterQuerySelector);
        const pagingContainer = getElement<HTMLElement>(Filter.PagingQuerySelector);
        const itemsContainer = getElement<HTMLElement>(Filter.ItemsQuerySelector);
        const sorterContainers = getElements<HTMLElement>(Filter.SortersQuerySelector);

        if (!itemsContainer) {
          throw new Error('Filter.RefreshData: Required items container not found.');
        }

        if (replaceProducts && itemsContainer) {
          itemsContainer.innerHTML = response.data.productsPartial;
        } else if (itemsContainer.children.length > 0) {
          itemsContainer.lastElementChild.insertAdjacentHTML('afterend', response.data.productsPartial);
        } else {
          itemsContainer.innerHTML = response.data.productsPartial;
        }
        if (filterContainer) {
          const expandedFilterRowIds = Array.from(filterContainer.querySelectorAll('li[data-state="open"]'))
            .filter(filterRow => filterRow.id)
            .map(filterRow => filterRow.id);

          filterContainer.innerHTML = response.data.filterPartial;

          expandedFilterRowIds.forEach(filterRowId => {
            const filterRow = document.getElementById(filterRowId);
            if (filterRow) {
              filterRow.setAttribute('data-state', 'open');
            }
          });
        }

        if (pagingContainer) {
          pagingContainer.innerHTML = response.data.pagingPartial;
        }

        if (sorterContainers) {
          sorterContainers.forEach(sorterContainer => {
            sorterContainer.innerHTML = response.data.sortPartial;
          });
        }

        document.dispatchEvent(new CustomEvent('filter.totalHitsUpdated', {
          bubbles: true,
          detail: { totalHits: response.data.totalHits }
        }));
        Filter.setFilterState();
        Filter.setUrlParams(QueryParams);

        const clearFilterLink = document.querySelector('[data-action="clear-filters"]');
        if (clearFilterLink) {
          if (QueryParams.checkedFilters.length > 0) {
            show(clearFilterLink, "flex");
          } else {
            hide(clearFilterLink);
          }
        }

        Filter.brandFilterTakeTop('details[data-key="Brand"]', Filter.brandItemsShown);

      }
      Filter.updateHierarchicalStatus();

      const hasPriceFilter = QueryParams.checkedFilters.some(f => f.key === "PriceForFacets");
      const onlyPriceFilter = QueryParams.checkedFilters.length === 1 && hasPriceFilter;

      if (!hasPriceFilter || onlyPriceFilter) {
        // reset the price filter slider when there is no active price-filter
        Filter.sliderInstance = null;
      }

      Filter.initPriceFilter();
    };

    private static getItems = async (pageId: string, productListName: string, queryParams: any, discountId: string = null): Promise<any> => {
      switch (Filter.FilterFor) {
        case FilterFor.Category:
        case FilterFor.Brand:
        case FilterFor.Search:
          return filterService.GetProducts(pageId, queryParams, FilterFor[Filter.FilterFor], productListName, discountId);
        case FilterFor.Articles:
          return filterService.GetArticles(pageId, queryParams);
        default:
          throw new Error(`Unhandled FilterFor type: ${Filter.FilterFor}`);
      }
    };

    private static getFilterData = (): filterService.IFilter[] => {

      const filterContainer = getElement<HTMLElement>(Filter.FilterQuerySelector);
      if (!filterContainer) {
        return [];
      }

      const filters: filterService.IFilter[] = [];

      filterContainer.querySelectorAll<HTMLElement>('[data-container="filter-group"]').forEach((filterGroupElement: HTMLElement) => {
        const key = filterGroupElement.getAttribute('data-key') ?? '';

        if (key === "PriceForFacets" && Filter.sliderInstance) {
          Filter.processPriceForFacetsFilter(filters, key);
        } else if (key === "A_AverageRating" && Filter.sliderInstance) {
          Filter.processRatingFilter(filters, filterGroupElement, key);
        } else {
          Filter.processDefaultFilter(filters, filterGroupElement, key);
        }
      });

      return filters;
    };

    private static processPriceForFacetsFilter(filters: filterService.IFilter[], key: string): void {

      const slider = document.getElementById('priceFilterSlider') as HTMLElement;
      const currentMinValue = Number(slider.getAttribute('data-current-min-value') ?? undefined); // active min-value
      const currentMaxValue = Number(slider.getAttribute('data-current-max-value') ?? undefined); // active max-value
      const minValue = Number(slider.getAttribute('data-min-value') ?? undefined); // minimum allowed value
      const maxValue = Number(slider.getAttribute('data-max-value') ?? undefined); // maximum allowed value

      if (currentMinValue !== minValue || currentMaxValue !== maxValue) {
        // Only run filter if the min and max values have changed.
        const priceFilterGreaterThan: filterService.IFilter = {
          key,
          operator: filterService.FilterOperator.Equal,
          values: [currentMinValue.toString(), currentMaxValue.toString()]
        };

        filters.push(priceFilterGreaterThan);
      }
    };

    private static processDefaultFilter(filters: filterService.IFilter[], filterGroupElement: HTMLElement, key: string): void {

      let anyValueChecked = false;
      const filter: filterService.IFilter = {
        key,
        operator: filterService.FilterOperator.Equal,
        values: []
      };

      filterGroupElement.querySelectorAll<HTMLInputElement>('[data-action="change-filter"]:checked').forEach((filterInputElement: HTMLInputElement) => {
        anyValueChecked = true;
        filter.values.push(filterInputElement.value);
      });

      if (anyValueChecked) {
        filters.push(filter);
      }
    };

    private static processRatingFilter(filters: filterService.IFilter[], filterGroupElement: HTMLElement, key: string): void {
      let anyValueChecked = false;
      const filter: filterService.IFilter = {
        key,
        operator: filterService.FilterOperator.Equal,
        values: []
      };

      filterGroupElement.querySelectorAll<HTMLInputElement>('[data-action="change-filter"]:checked').forEach((filterInputElement: HTMLInputElement) => {
        anyValueChecked = true;
        filterInputElement.value.split("_")
          .forEach(item => {
            item = item.replace(",", ".").trim();
            filter.values.push(item);
          });
      });

      if (anyValueChecked) {
        filters.push(filter);
      }
    }

    private static clearFilters = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();

      let eventElement: HTMLElement = e.target as HTMLElement;
      let pageId: string | undefined = eventElement.getAttribute('data-page-id') ?? undefined;
      if (!pageId) {
        pageId = eventElement.parentElement?.getAttribute('data-page-id') ?? undefined;
      }
      if (!pageId)
        return false;

      const filterContainer = getElement<HTMLElement>(Filter.FilterQuerySelector);
      const filterCheckboxes = filterContainer.querySelectorAll('input:checked') as NodeListOf<HTMLInputElement>;
      const productListName: string = Filter.GetProductListName();
      let needsRefresh = true;

      if (!filterCheckboxes || filterCheckboxes.length < 1) {
        needsRefresh = false;
      }

      if (Filter.sliderInstance) {
        Filter.sliderInstance = null;
        needsRefresh = true;
      }

      if (needsRefresh === false) {
        return false;
      }

      filterCheckboxes?.forEach(checkbox => {
        checkbox = checkbox as HTMLInputElement;
        checkbox.checked = false;
      });

      Filter.Page = 1;
      Filter.RefreshData(pageId, productListName, true, null).catch(console.error);

      if (!eventElement.hasAttribute('data-action')) {
        eventElement = eventElement.parentElement;
      }
      hide(eventElement);

      return false;
    };

    private static clearSingleFilter = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();

      let eventElement: HTMLElement = e.target as HTMLElement;
      let pageId: string | undefined = eventElement.getAttribute('data-page-id') ?? undefined;
      if (!pageId) {
        pageId = eventElement.parentElement?.getAttribute('data-page-id') ?? undefined;
      }
      if (!pageId)
        return false;

      const filterContent = eventElement.closest('.filter-content');
      const filterCheckboxes = filterContent.querySelectorAll('input:checked') as NodeListOf<HTMLInputElement>;
      const productListName: string = Filter.GetProductListName();
      let needsRefresh = true;

      if (!filterCheckboxes || filterCheckboxes.length < 1) {
        needsRefresh = false;
      }

      if (Filter.sliderInstance) {
        Filter.sliderInstance = null;
        needsRefresh = true;
      }

      const inputElement = filterContent.querySelector('input[type="text"][data-action="category-filter-search-input"]') as HTMLInputElement;
      if (inputElement && inputElement.value) {
        inputElement.value = '';
        delete Filter.filterState[inputElement.id];
        needsRefresh = true;
      }

      if (needsRefresh === false) {
        return false;
      }

      filterCheckboxes?.forEach(checkbox => {
        checkbox = checkbox as HTMLInputElement;
        checkbox.checked = false;
      });

      Filter.Page = 1;
      Filter.RefreshData(pageId, productListName, true, null).catch(console.error);

      if (!eventElement.hasAttribute('data-action')) {
        eventElement = eventElement.parentElement;
      }
      hide(eventElement);

      return false;
    };

    private static changeSort = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();

      const input: HTMLSelectElement = e.target as HTMLSelectElement;
      const pageId: string | undefined = input.getAttribute('data-page-id') ?? undefined;
      const selectedValue = input.value;
      const sortKey = selectedValue.split('-')[0];
      const direction = selectedValue.split('-')[1];
      const productListName: string = Filter.GetProductListName();

      Filter.Page = 1;
      Filter.Sorter = {
        key: sortKey,
        sortDirection: direction
      };
      Filter.RefreshData(pageId, productListName, true, null).catch(console.error);
    };

    private static saveFilterState = () => {

      const saveElementsState = getElements<HTMLElement>('[data-save-state]');

      if (saveElementsState && saveElementsState.length > 0) {
        Filter.elementsStates = [];
        const tempStates: IElementState[] = [];
        saveElementsState.forEach(item => {
          const elementState = Filter.resolveStateAndValue(item);
          if (elementState) {
            tempStates.push(elementState);
          }
        });

        // Arrange states so opens occur before scrolls.
        tempStates.forEach(state => {
          if (state.stateType !== StateType.Scroll)
            Filter.elementsStates.push(state);
        });
        tempStates.forEach(state => {
          if (state.stateType === StateType.Scroll)
            Filter.elementsStates.push(state);
        });
      }
    };

    private static setFilterState = () => {
      if (!Filter.elementsStates) {
        return;
      }
      Filter.elementsStates.forEach(elementState => {
        const domElement = getElement<HTMLElement>(`#${elementState.elementId}`);
        if (!domElement) {
          return;
        }

        switch (elementState.stateType) {
          case StateType.Open:
            if (domElement.nodeName.toLocaleLowerCase() === 'details' && elementState.value) {
              domElement.setAttribute("open", "true");
            }
            break;
          case StateType.Scroll:
            domElement.scrollTop = elementState.value;
            break;
          default:
            throw new Error(`Unhandled stateType type: ${elementState.stateType}`);
        }
      });
      Filter.restoreSearchStateV2();
    };

    private static resolveStateAndValue = (htmlElement: HTMLElement): IElementState => {

      const elementState = {} as IElementState;
      if (!htmlElement.id)
        return null;

      elementState.elementId = htmlElement.id;
      const stateType: StateType = StateType[htmlElement.getAttribute('data-save-state') as keyof typeof StateType];
      const elementType = htmlElement.nodeName.toLowerCase();

      switch (stateType) {
        case StateType.Open: {
          elementState.stateType = StateType.Open;
          if (elementType === 'details') {
            elementState.value = htmlElement.hasAttribute("open");
          }
          break;
        }
        case StateType.Scroll: {
          elementState.stateType = StateType.Scroll;
          elementState.value = htmlElement.scrollTop;
          break;
        }
        default:
          throw new Error(`Unhandled stateType type: ${elementState.stateType}`);
      }
      return elementState;
    };

    private static setUrlParams = (queryParams: filterService.IQuery) => {

      const urlQuery = new URLSearchParams(window.location.search);

      if (queryParams.searchString) {
        urlQuery.set("q", queryParams.searchString);
      } else {
        urlQuery.delete("q");
      }

      if (queryParams.page > 1) {
        urlQuery.set("p", queryParams.page.toString());
      } else {
        urlQuery.delete("p");
      }

      if (queryParams.checkedFilters.length > 0) {
        let filterQuery = "";
        queryParams.checkedFilters.forEach(filter => {
          filterQuery += `${filter.key.toString()}:`;
          filterQuery += `${filter.values.join("|")};`;
        });

        urlQuery.set("f", filterQuery);
      } else {
        urlQuery.delete("f");
      }

      if (queryParams.sorter != null) {
        urlQuery.set("s", queryParams.sorter.key);
        urlQuery.set("d", queryParams.sorter.sortdirection.toString());
      } else {
        urlQuery.delete("s");
        urlQuery.delete("d");
      }
      if (urlQuery.toString().length > 0) {
        history.replaceState(null, null, `${window.location.pathname}?${urlQuery.toString()}`);
      } else {
        history.replaceState(null, null, window.location.origin + window.location.pathname);
      }
    };

    private static toggleFilterChildren = (e: Event) => {

      const target = e.target as HTMLElement;
      const parentLi = target.closest('li') as ListUtils.ExtendedHTMLLIElement;
      const currentState: ListUtils.ListState = parentLi.getState() as ListUtils.ListState;

      switch (currentState) {
        case ListUtils.ListState.Open:
          parentLi.setAttribute('data-state', 'closed');
          Filter.filterState[parentLi.id] = 'closed';
          break;
        case ListUtils.ListState.Closed:
          parentLi.setAttribute('data-state', 'open');
          Filter.filterState[parentLi.id] = 'open';
          break;
        default:
          parentLi.setState(ListUtils.ListState.Open);
          break;
      }
    };

    private static updateHierarchicalStatus = () => {

      // Find all checked checkboxes.
      const checkedCheckboxes: NodeListOf<HTMLInputElement> = document.querySelectorAll(`${Filter.FilterCheckboxQuerySelector}:checked`);

      // Process each checked checkbox.
      checkedCheckboxes.forEach((checkbox: HTMLInputElement) => {
        const currentLi = checkbox.closest('li');
        if (currentLi) {
          Filter.updateParentCheckboxState(currentLi);
        }
      });
    };

    private static updateParentCheckboxState = (childLi: HTMLElement): void => {

      let currentLi = childLi;

      while (currentLi) {
        const parentUl = currentLi.closest('ul.filter-child-rows');
        if (!parentUl) {
          break;
        }

        const parentLi = parentUl.closest('li');
        if (!parentLi) {
          break;
        }

        const parentCheckbox = parentLi.querySelector(Filter.FilterCheckboxQuerySelector) as HTMLInputElement;
        if (!parentCheckbox) {
          break;
        }

        // Get all child checkboxes in the current level.
        const childCheckboxes = parentUl.querySelectorAll(Filter.FilterCheckboxQuerySelector);
        const checkedChildCheckboxes = parentUl.querySelectorAll(`${Filter.FilterCheckboxQuerySelector}:checked`);

        if (checkedChildCheckboxes.length === childCheckboxes.length) {
          // All child checkboxes are checked.
          parentCheckbox.checked = true;
          parentCheckbox.indeterminate = false;
          parentCheckbox.setAttribute('data-state', 'checked');
        } else if (checkedChildCheckboxes.length > 0) {
          // Some child checkboxes are checked.
          parentCheckbox.checked = false;
          parentCheckbox.indeterminate = true;
          parentCheckbox.setAttribute('data-state', 'indeterminate');
        } else {
          // No child checkboxes are checked.
          parentCheckbox.checked = false;
          parentCheckbox.indeterminate = false;
          parentCheckbox.setAttribute('data-state', '');
        }

        // Move up the hierarchy
        currentLi = parentLi;
      }
    };

    private static brandFilterTakeTop = (selector: string, visibleItemsCount: number) => {

      // Find all <details> elements matching the selector.
      const detailsElements = document.querySelectorAll(selector);

      // Ensure there are matching elements.
      if (detailsElements.length > 0) {
        detailsElements.forEach(detailsElement => {
          // Get the <ul> and the <button> within the current <details> element.
          const list = detailsElement.querySelector('ul');
          const button = detailsElement.querySelector('button');

          // Ensure both list and button exist.
          if (list && button) {
            const listItems = list.querySelectorAll('li');

            // Initially display the specified number of items.
            if (Number.isFinite(visibleItemsCount)) {
              listItems.forEach((item, index) => {
                if (index < visibleItemsCount) {
                  item.classList.remove('hidden');
                } else {
                  item.classList.add('hidden');
                }
              });
            } else {
              listItems.forEach((item) => {
                item.classList.remove('hidden');
              });
            }

            // Add click event listener to the button.
            button.addEventListener('click', () => {
              const isExpanded = button.getAttribute('data-expanded') === 'true';

              if (isExpanded) {
                // Hide all items except the specified number.
                listItems.forEach((item, index) => {
                  if (index < visibleItemsCount) {
                    item.classList.remove('hidden');
                  } else {
                    item.classList.add('hidden');
                  }
                });
                button.setAttribute('data-expanded', 'false');
              } else {
                // Show all items.
                listItems.forEach(item => {
                  item.classList.remove('hidden');
                });
                button.setAttribute('data-expanded', 'true');
              }
            });

            // Update button visibility.
            Filter.updateBrandFilterButtons(visibleItemsCount);
          }
        });
      }
    };

    private static updateBrandFilterButtons = (limit: number) => {

      const showAllBtn = document.querySelector('[data-action="brand-filter-show-all"]') as HTMLElement;
      const showLessBtn = document.querySelector('[data-action="brand-filter-show-less"]') as HTMLElement;

      if (Number.isFinite(limit)) {
        show(showAllBtn);
        hide(showLessBtn);
      } else {
        hide(showAllBtn);
        show(showLessBtn);
      }
    };

    private static updateBrandDisplayLimit = (limit: number, htmlElement: HTMLElement) => {

      // Update button visibility.
      Filter.updateBrandFilterButtons(limit);

      const pageId: string | undefined = htmlElement.getAttribute('data-page-id') ?? undefined;
      const productListName: string = Filter.GetProductListName();

      Filter.brandItemsShown = limit;

      Filter.RefreshData(pageId, productListName, true, null).catch(console.error);
    };

    private static onBrandFilterShowAll = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();

      Filter.updateBrandDisplayLimit(Infinity, e.target as HTMLElement);
    };

    private static onBrandFilterShowLess = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();

      Filter.updateBrandDisplayLimit(Filter.defaultBrandItemsShown, e.target as HTMLElement);
    };
  }
}

export default Easyfy.Modules;
